import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
    untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Textarea } from 'primeng/textarea';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { AdminGame, FieldUpdate } from '@/lib-backend';
import * as game from '@/lib-backend/methods/game';
import { RealmGamesService } from '@/app/services/realm-games.service';
import { OverrideField } from '@/app/models/override-field.model';
import { withResolvedGameKind } from '@/app/utils/game';
import { RealmGameArtPanel } from './realm-game-art-panel';

/**
 * Per-realm override fields edited on the General tab. Art (icon/banner/images)
 * is NOT here — it lives on the Art tab, which applies immediately.
 */
interface EditDraft {
    Title: OverrideField<string>;
    Description: OverrideField<string>;
    IsActive: OverrideField<boolean>;
    IsFeatured: OverrideField<boolean>;
}

@Component({
    selector: 'app-edit-game-dialog',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Checkbox,
        Dialog,
        InputText,
        Message,
        Textarea,
        Tab,
        TabList,
        TabPanel,
        TabPanels,
        Tabs,
        RealmGameArtPanel,
    ],
    templateUrl: './edit-game-dialog.html',
})
export class EditGameDialog {
    @Input({ required: true }) set visible(v: boolean) {
        this.visibleSignal.set(v);
    }

    @Input() set adminGame(g: AdminGame | null) {
        this.editingGame.set(g);
    }

    /** The realm's site template id — keys the Art tab's finalized variants. */
    @Input() set siteTemplateId(id: string | null) {
        this.siteTemplateIdSignal.set(id);
    }

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() saved = new EventEmitter<void>();

    protected readonly gamesService = inject(RealmGamesService);

    protected readonly visibleSignal = signal(false);
    protected readonly editingGame = signal<AdminGame | null>(null);
    protected readonly siteTemplateIdSignal = signal<string | null>(null);
    protected readonly draft = signal<EditDraft>(this.makeEmptyDraft());
    private readonly initialDraft = signal<EditDraft>(this.makeEmptyDraft());

    protected readonly activeTab = signal<'general' | 'art'>('general');

    protected readonly saving = signal(false);
    protected readonly saveError = signal<string | null>(null);

    /** Only the General override fields are saved here; art applies on its tab. */
    protected readonly canSave = computed(
        () =>
            JSON.stringify(this.draft()) !==
            JSON.stringify(this.initialDraft()),
    );

    /** The Art tab loads only while shown. */
    protected readonly artActive = computed(
        () => this.activeTab() === 'art' && !!this.editingGame(),
    );

    constructor() {
        // Rebuild the General draft only when the dialog opens — not on every
        // editingGame change (an art apply updates editingGame mid-session and
        // must not discard unsaved General edits). editingGame is read untracked.
        effect(() => {
            if (this.visibleSignal()) {
                const g = untracked(() => this.editingGame());
                if (g) {
                    const initial = this.gameToDraft(g);
                    this.draft.set(structuredClone(initial));
                    this.initialDraft.set(structuredClone(initial));
                    this.saveError.set(null);
                    this.activeTab.set('general');
                }
            }
        });
    }

    protected toggleOverride<K extends keyof EditDraft>(
        key: K,
        value: boolean,
    ): void {
        const g = this.editingGame();
        if (!g) return;
        this.draft.update((d) => {
            const next = { ...d };
            if (value && !d[key].override) {
                next[key] = {
                    override: true,
                    value: this.effectiveTemplateFieldValue(g, key),
                } as EditDraft[K];
            } else {
                next[key] = { ...d[key], override: value } as EditDraft[K];
            }
            return next;
        });
    }

    protected setOverrideValue<K extends keyof EditDraft>(
        key: K,
        value: EditDraft[K]['value'],
    ): void {
        this.draft.update((d) => ({
            ...d,
            [key]: { ...d[key], value } as EditDraft[K],
        }));
    }

    protected onVisibleChange(value: boolean): void {
        this.visibleSignal.set(value);
        this.visibleChange.emit(value);
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    /** Reflect an art change (applied immediately on the Art tab) locally. */
    protected onGameUpdated(g: AdminGame): void {
        // updateGame doesn't re-resolve GameKind — keep the prior resolved one
        // so the Art tab's AI generation can still resolve the category.
        this.editingGame.set(
            withResolvedGameKind(
                g,
                this.editingGame()?.Template.Template.GameKind ?? null,
            ),
        );
        this.saved.emit();
    }

    protected templatePreview(key: keyof EditDraft): string {
        const g = this.editingGame();
        if (!g) return '';
        const t = g.Template.Template;
        switch (key) {
            case 'Title':
                return t.Title;
            case 'Description':
                return t.Description;
            case 'IsActive':
                return t.IsActive ? 'Yes' : 'No';
            case 'IsFeatured':
                return t.IsFeatured ? 'Yes' : 'No';
        }
    }

    protected async save(): Promise<void> {
        const g = this.editingGame();
        if (!g || !this.canSave()) return;
        this.saving.set(true);
        this.saveError.set(null);
        try {
            const result = await this.gamesService.updateGame(
                this.buildUpdateRequest(g),
            );
            if (result.kind === 'ok') {
                this.saved.emit();
                this.close();
            } else {
                this.saveError.set(this.formatError(result.reason));
            }
        } finally {
            this.saving.set(false);
        }
    }

    private buildUpdateRequest(g: AdminGame): game.UpdateGameRequest {
        const d = this.draft();
        return {
            RealmId: g.RealmId,
            GameId: g.Id,
            Title: this.diffString(g.Overrides.Title, d.Title),
            Description: this.diffString(g.Overrides.Description, d.Description),
            // Art is managed by the Art tab (applyArtOverrides) — leave untouched.
            BannerUrl: null,
            IconUrl: null,
            Images: null,
            IsActive: this.diffBool(g.Overrides.IsActive, d.IsActive),
            IsFeatured: this.diffBool(g.Overrides.IsFeatured, d.IsFeatured),
        };
    }

    private diffString(
        original: string | null,
        form: OverrideField<string>,
    ): FieldUpdate<string | null> {
        if (!form.override) {
            return original !== null ? { value: null } : null;
        }
        if (original === form.value) return null;
        return { value: form.value };
    }

    private diffBool(
        original: boolean | null,
        form: OverrideField<boolean>,
    ): FieldUpdate<boolean | null> {
        if (!form.override) {
            return original !== null ? { value: null } : null;
        }
        if (original === form.value) return null;
        return { value: form.value };
    }

    private gameToDraft(g: AdminGame): EditDraft {
        const o = g.Overrides;
        const t = g.Template.Template;
        return {
            Title: { override: o.Title !== null, value: o.Title ?? t.Title },
            Description: {
                override: o.Description !== null,
                value: o.Description ?? t.Description,
            },
            IsActive: {
                override: o.IsActive !== null,
                value: o.IsActive ?? t.IsActive,
            },
            IsFeatured: {
                override: o.IsFeatured !== null,
                value: o.IsFeatured ?? t.IsFeatured,
            },
        };
    }

    private effectiveTemplateFieldValue<K extends keyof EditDraft>(
        g: AdminGame,
        key: K,
    ): EditDraft[K]['value'] {
        const t = g.Template.Template;
        switch (key) {
            case 'Title':
                return t.Title as EditDraft[K]['value'];
            case 'Description':
                return t.Description as EditDraft[K]['value'];
            case 'IsActive':
                return t.IsActive as EditDraft[K]['value'];
            case 'IsFeatured':
                return t.IsFeatured as EditDraft[K]['value'];
        }
        return '' as EditDraft[K]['value'];
    }

    private makeEmptyDraft(): EditDraft {
        const empty: OverrideField<string> = { override: false, value: '' };
        const emptyBool: OverrideField<boolean> = {
            override: false,
            value: false,
        };
        return {
            Title: { ...empty },
            Description: { ...empty },
            IsActive: { ...emptyBool },
            IsFeatured: { ...emptyBool },
        };
    }

    private formatError(reason: string): string {
        switch (reason) {
            case 'NotFound':
                return 'Game not found.';
            case 'GameKindNotFound':
                return 'Game kind not registered on the game server.';
            default:
                return 'Save failed. Please try again.';
        }
    }
}
